import { Grid } from '@mui/material'
import { useTranslations } from '@fenetech/translations';
import Constants from 'helpers/constants';
import TextFieldForm from "components/Common/TextFieldForm";
import useQuoteData from "../useQuoteData";
import { QuoteRequiredFieldEnum } from "helpers/enums";
import { useCallback, useMemo } from "react";
import { useFormContext } from "react-hook-form";
import { isNullOrWhiteSpace } from "helpers/objects";

interface IProps {
    addressPrefix: string,
    readOnly: boolean,
    hideAllFields: boolean
}

const enum AddressFieldTypeEnum {
    Address = 0,
    Phone = 1,
    Fax = 2
}

const showCountry: boolean = false;

function SelectAddress({ addressPrefix, readOnly, hideAllFields }: IProps) {

    const tm = useTranslations();
    const { requiredFields } = useQuoteData();
    const { getValues } = useFormContext();

    const fieldIsRequired = useCallback((addressFieldType: AddressFieldTypeEnum) => {
        if (!requiredFields || requiredFields.length === 0) {
            return false;
        }
        let checkEnum: QuoteRequiredFieldEnum;

        if (addressPrefix === "shipping") {
            checkEnum = {
                [AddressFieldTypeEnum.Address]: QuoteRequiredFieldEnum.ShippingAddress,
                [AddressFieldTypeEnum.Phone] : QuoteRequiredFieldEnum.ShippingPhone,
                [AddressFieldTypeEnum.Fax]: QuoteRequiredFieldEnum.ShippingFax
            }[addressFieldType];
        } else {
            checkEnum = {
                [AddressFieldTypeEnum.Address]: QuoteRequiredFieldEnum.BillingAddress,
                [AddressFieldTypeEnum.Phone] : QuoteRequiredFieldEnum.BillingPhone,
                [AddressFieldTypeEnum.Fax]: QuoteRequiredFieldEnum.BillingFax
            }[addressFieldType];
        }

        return requiredFields.includes(checkEnum);
    }, [requiredFields, addressPrefix]);

    const getAddressFieldIsValidFunction = useCallback((fieldName: string, fieldNameBaseContent: string, addressFieldType: AddressFieldTypeEnum) => {

        const checkFunction = (value: any) => {
            if (!fieldIsRequired(addressFieldType)) {
                return true;
            }

            if (addressPrefix === "billing") {
                //If billing is checked same as shipping, we need to base on shipping's value
                if (getValues("billing.isAddressSameAsShipping")) {
                    value = getValues("shipping" + fieldName);
                }
            }

            return !isNullOrWhiteSpace(value) || tm.GetWithParams("{0} cannot be blank.", tm.Get(fieldNameBaseContent));
        }

        return checkFunction;
    }, [fieldIsRequired, addressPrefix, getValues, tm]);

    const addressFieldsRequired = useMemo(() => {
        return fieldIsRequired(AddressFieldTypeEnum.Address);
    }, [fieldIsRequired]);

    const phoneFieldRequired = useMemo(() => {
        return fieldIsRequired(AddressFieldTypeEnum.Phone);
    }, [fieldIsRequired]);

    const faxFieldRequired = useMemo(() => {
        return fieldIsRequired(AddressFieldTypeEnum.Fax);
    }, [fieldIsRequired]);

    return <>
        <Grid container spacing={2} padding={0}>
            <Grid item xs={12} sx={{display: hideAllFields ? "none" : "block"}}>
                <TextFieldForm
                    fieldName={addressPrefix + ".name"}
                    readOnly={false}
                    id="name"
                    variant="standard"
                    inputProps={{ readOnly: readOnly, maxLength: Constants.MaxLength.AddressName }}
                    placeholder={tm.Get("Name")}
                    label={tm.Get("Name")}
                    disabled={readOnly}
                    fullWidth
                />
            </Grid>
            <Grid item xs={12} sm={6} sx={{display: hideAllFields ? "none" : "block"}}>
                <TextFieldForm
                    fieldName={addressPrefix + ".address.address1"}
                    readOnly={false}
                    id="address1"
                    variant="standard"
                    inputProps={{ readOnly: readOnly, maxLength: Constants.MaxLength.Address1 }}
                    validateValue={getAddressFieldIsValidFunction(".address.address1", "Address 1", AddressFieldTypeEnum.Address)}
                    showRequiredAsterisk={addressFieldsRequired}
                    label={tm.Get("Address 1")}
                    disabled={readOnly}
                    fullWidth
                />
            </Grid>
            <Grid item xs={12} sm={6} sx={{display: hideAllFields ? "none" : "block"}}>
                <TextFieldForm
                    fieldName={addressPrefix + ".address.address2"}
                    readOnly={false}
                    id="address2"
                    variant="standard"
                    inputProps={{ readOnly: readOnly, maxLength: Constants.MaxLength.Address2 }}
                    label={tm.Get("Address 2")}
                    disabled={readOnly}
                    fullWidth
                />
            </Grid>
            <Grid item xs={12} sm={6} sx={{display: hideAllFields ? "none" : "block"}}>
                <TextFieldForm
                    fieldName={addressPrefix + ".address.city"}
                    readOnly={false}
                    id="city"
                    variant="standard"
                    inputProps={{ readOnly: readOnly, maxLength: Constants.MaxLength.City }}
                    validateValue={getAddressFieldIsValidFunction(".address.city", "City", AddressFieldTypeEnum.Address)}
                    showRequiredAsterisk={addressFieldsRequired}
                    placeholder={tm.Get("City")}
                    label={tm.Get("City")}
                    disabled={readOnly}
                    fullWidth
                />
            </Grid>
            <Grid item xs={12} sm={2} sx={{display: hideAllFields ? "none" : "block"}}>
                <TextFieldForm
                    fieldName={addressPrefix + ".address.state"}
                    readOnly={false}
                    id="state"
                    variant="standard"
                    inputProps={{ readOnly: readOnly, maxLength: Constants.MaxLength.State }}
                    validateValue={getAddressFieldIsValidFunction(".address.state", "State", AddressFieldTypeEnum.Address)}
                    showRequiredAsterisk={addressFieldsRequired}
                    placeholder={tm.Get("State")}
                    label={tm.Get("State")}
                    disabled={readOnly}
                    fullWidth
                />
            </Grid>
            <Grid item xs={12} sm={4} sx={{display: hideAllFields ? "none" : "block"}}>
                <TextFieldForm
                    fieldName={addressPrefix + ".address.zipCode"}
                    readOnly={false}
                    id="zipCode"
                    variant="standard"
                    inputProps={{ readOnly: readOnly, maxLength: Constants.MaxLength.ZipCode }}
                    validateValue={getAddressFieldIsValidFunction(".address.zipCode", "Zip Code", AddressFieldTypeEnum.Address)}
                    showRequiredAsterisk={addressFieldsRequired}
                    placeholder={tm.Get("Zip Code")}
                    label={tm.Get("Zip Code")}
                    disabled={readOnly}
                    fullWidth
                />
            </Grid>
            <Grid item xs={12} sm={6} sx={{display: hideAllFields ? "none" : "block"}}>
                <TextFieldForm
                    fieldName={addressPrefix + ".phone"}
                    readOnly={false}
                    id="phone"
                    variant="standard"
                    inputProps={{ readOnly: readOnly, maxLength: Constants.MaxLength.Phone }}
                    validateValue={getAddressFieldIsValidFunction(".phone", "Phone", AddressFieldTypeEnum.Phone)}
                    showRequiredAsterisk={phoneFieldRequired}
                    placeholder={tm.Get("Phone")}
                    label={tm.Get("Phone")}
                    disabled={readOnly}
                    fullWidth
                />
            </Grid>
            <Grid item xs={12} sm={6} sx={{display: hideAllFields ? "none" : "block"}}>
                <TextFieldForm
                    fieldName={addressPrefix + ".fax"}
                    readOnly={false}
                    id="fax"
                    variant="standard"
                    inputProps={{ readOnly: readOnly, maxLength: Constants.MaxLength.Fax }}
                    validateValue={getAddressFieldIsValidFunction(".fax", "Fax", AddressFieldTypeEnum.Fax)}
                    showRequiredAsterisk={faxFieldRequired}
                    placeholder={tm.Get("Fax")}
                    label={tm.Get("Fax")}
                    disabled={readOnly}
                    fullWidth
                />
            </Grid>
            {(showCountry && !hideAllFields) &&
                <Grid item xs={12} sm={6}>
                    <TextFieldForm
                        fieldName={addressPrefix + ".address.country"}
                        readOnly={false}
                        id="country"
                        variant="standard"
                        inputProps={{ readOnly: readOnly, maxLength: Constants.MaxLength.Country }}
                        placeholder={tm.Get("Country")}
                        label={tm.Get("Country")}
                        disabled={readOnly}
                        fullWidth
                    />
                </Grid>
            }
        </Grid>
    </>
}

export default SelectAddress;